import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  LeadEnrichment,
  Lead,
  TimberFrameBuyerSignal,
  PersonEnrichment,
} from '../../types';
import { useCRM } from '../../utils/CRMContext';
import { enrichLead, getScoreBandColor, getScoreBandLabel, getCapacityColor, formatCurrency } from '../../utils/enrichmentService';
import { logAgentActivityForLead } from '../../utils/portalActivityLogger';
import {
  Loader2,
  Sparkles,
  MapPin,
  Briefcase,
  Linkedin,
  Globe2,
  DollarSign,
  GraduationCap,
  AlertTriangle,
  RefreshCw,
  X,
  ChevronRight,
  BarChart3,
  Eye,
  Clock,
  Home,
  Facebook,
  Instagram,
  Twitter,
  Youtube,
  Building2,
  Newspaper,
  Users,
} from 'lucide-react';
import './LeadIntelligencePanel.css';

interface LeadIntelligencePanelProps {
  lead: Lead;
}

export const LeadIntelligencePanel: React.FC<LeadIntelligencePanelProps> = ({ lead }) => {
  const { updateLead, enhancedDeals } = useCRM();
  const [enrichment, setEnrichment] = useState<LeadEnrichment | null>(lead.enrichment || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeModal, setActiveModal] = useState<string | null>(null);

  const handleEnrich = useCallback(async () => {
    if (!lead.id) return;
    setLoading(true);
    setError(null);
    try {
      const result = await enrichLead(lead, enhancedDeals);
      setEnrichment(result);
      await updateLead(lead.id, { enrichment: result } as any);
      // Surface the agent run in the workspace activity feed (no-op without a portal).
      void logAgentActivityForLead(lead.id, 'enrichment_completed', {
        score: result.score?.total,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Enrichment failed');
    } finally {
      setLoading(false);
    }
  }, [lead, enhancedDeals, updateLead]);

  useEffect(() => {
    if (lead.enrichment) {
      setEnrichment(lead.enrichment);
    }
  }, [lead.enrichment]);

  if (!enrichment && !loading) {
    return (
      <div className="lip2-empty">
        <Sparkles size={22} className="lip2-empty-icon" />
        <span className="lip2-empty-text">Lead Intelligence</span>
        <button className="lip2-enrich-btn" onClick={handleEnrich} disabled={loading}>
          <Sparkles size={13} />
          Enrich Lead
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="lip2-loading">
        <Loader2 size={18} className="lip2-spin" />
        <span>Marketing agent is enriching this lead…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="lip2-error">
        <AlertTriangle size={14} />
        <span>{error}</span>
        <button onClick={handleEnrich} className="lip2-retry-btn">Retry</button>
      </div>
    );
  }

  if (!enrichment) return null;

  const { score, buyerSignals, person, property, ownership, buildSite, behavioral, financialCapacity, talkingPoints, historicalPattern, avm, saleHistory, dataSources, _attomError, permits, phoneIntel, neighborhoodData } = enrichment;

  // Guard: score can be missing in partial/stale enrichment
  if (!score?.band) {
    return (
      <div className="lip2-empty">
        <Sparkles size={22} className="lip2-empty-icon" />
        <span className="lip2-empty-text">Lead Intelligence</span>
        <p className="lip2-empty-hint" style={{ fontSize: 12, color: 'var(--gray-500)', margin: '8px 0' }}>Score data missing. Re-enrich to refresh.</p>
        <button className="lip2-enrich-btn" onClick={handleEnrich} disabled={loading}>
          <Sparkles size={13} />
          Enrich Lead
        </button>
      </div>
    );
  }

  const bandColor = getScoreBandColor(score.band);
  const enrichDate = parseFirestoreDate(enrichment.enrichedAt);
  const timeAgo = getTimeAgo(enrichDate);
  const hasPropertyData = !!(property || avm || ownership || saleHistory?.length || buildSite);
  const hasEngagement = !!(behavioral && (behavioral.totalPagesViewed > 0 || behavioral.returnVisitCount > 0));

  return (
    <div className="lip2-root">
      {/* Score + Person headline */}
      <div className="lip2-hero">
        <div className="lip2-score-pill" style={{ background: bandColor }}>
          {score.total}
        </div>
        <div className="lip2-hero-info">
          <div className="lip2-hero-band" style={{ color: bandColor }}>
            {getScoreBandLabel(score.band)}
          </div>
          <div className="lip2-hero-meta">{timeAgo}</div>
        </div>
        <div className="lip2-hero-actions">
          {dataSources && dataSources.length > 0 && (
            <div className="lip2-sources">
              {dataSources.map(src => (
                <span key={src} className="lip2-src-tag">{src.toUpperCase()}</span>
              ))}
            </div>
          )}
          <button className="lip2-refresh" onClick={handleEnrich} title="Re-enrich">
            <RefreshCw size={12} />
          </button>
        </div>
      </div>

      {/* ATTOM notice - compact */}
      {_attomError && !dataSources?.includes('attom') && (
        <div className="lip2-notice">
          <AlertTriangle size={11} />
          <span>{_attomError}</span>
        </div>
      )}

      {/* Person card */}
      {person && (person.jobTitle || person.linkedinUrl) && (
        <PersonCard person={person} leadName={`${lead.firstName} ${lead.lastName}`.trim()} onViewMore={() => setActiveModal('person')} />
      )}

      {/* Signal pills - compact row */}
      {buyerSignals.length > 0 && (
        <div className="lip2-signals">
          {buyerSignals.slice(0, 4).map(s => (
            <span key={s.id} className="lip2-signal" title={s.description}>
              <span className="lip2-signal-dot" style={{ background: getCategoryColor(s.category) }} />
              {s.label}
            </span>
          ))}
          {buyerSignals.length > 4 && (
            <button className="lip2-signal lip2-signal-more" onClick={() => setActiveModal('signals')}>
              +{buyerSignals.length - 4} more
            </button>
          )}
        </div>
      )}

      {/* Quick stat row */}
      <div className="lip2-stats-row">
        {(financialCapacity || neighborhoodData) && (
          <button className="lip2-stat-card" onClick={() => setActiveModal('financial')}>
            <DollarSign size={13} className="lip2-stat-icon" />
            <span className="lip2-stat-label">Financial</span>
            <span className="lip2-stat-value" style={{ color: financialCapacity ? getCapacityColor(financialCapacity.capacityLevel) : '#64748b' }}>
              {financialCapacity?.combinedAssets && financialCapacity.combinedAssets > 0
                ? formatCompact(financialCapacity.combinedAssets)
                : neighborhoodData?.medianHomeValue
                  ? `~${formatCompact(neighborhoodData.medianHomeValue)}`
                  : financialCapacity?.capacityLevel || 'View'}
            </span>
            <ChevronRight size={10} className="lip2-stat-arrow" />
          </button>
        )}
        {hasPropertyData && (
          <button className="lip2-stat-card" onClick={() => setActiveModal('property')}>
            <Home size={13} className="lip2-stat-icon" />
            <span className="lip2-stat-label">Property</span>
            <span className="lip2-stat-value">
              {avm?.value ? formatCompact(avm.value) : property?.lotSizeAcres ? `${property.lotSizeAcres} ac` : 'View'}
            </span>
            <ChevronRight size={10} className="lip2-stat-arrow" />
          </button>
        )}
        {hasEngagement && (
          <button className="lip2-stat-card" onClick={() => setActiveModal('engagement')}>
            <Eye size={13} className="lip2-stat-icon" />
            <span className="lip2-stat-label">Activity</span>
            <span className="lip2-stat-value">{behavioral!.totalPagesViewed} pages</span>
            <ChevronRight size={10} className="lip2-stat-arrow" />
          </button>
        )}
      </div>

      {/* Historical pattern - inline */}
      {historicalPattern && historicalPattern.sampleSize > 0 && (
        <div className="lip2-hist">
          <span className="lip2-hist-pct">{Math.round(historicalPattern.similarLeadCloseRate * 100)}%</span>
          <span className="lip2-hist-txt">close rate for similar leads</span>
          <span className="lip2-hist-detail">n={historicalPattern.sampleSize} · avg {historicalPattern.averageDaysToClose}d · {formatCurrency(historicalPattern.averageDealValue)} avg</span>
        </div>
      )}

      {/* Talking points - always visible, most actionable */}
      {talkingPoints.length > 0 && (
        <div className="lip2-briefs">
          <div className="lip2-briefs-label">Sales Brief</div>
          {talkingPoints.slice(0, 3).map((tp, i) => (
            <div key={i} className={`lip2-brief lip2-brief-${tp.category}`}>
              {tp.text}
            </div>
          ))}
          {talkingPoints.length > 3 && (
            <button className="lip2-brief-more" onClick={() => setActiveModal('briefs')}>
              +{talkingPoints.length - 3} more points
            </button>
          )}
        </div>
      )}

      {/* Score breakdown - compact */}
      <button className="lip2-breakdown-trigger" onClick={() => setActiveModal('score')}>
        <BarChart3 size={12} />
        <span>Score Breakdown</span>
        <ChevronRight size={11} />
      </button>

      {/* ═══ Modals ═══ */}
      {activeModal && (
        <DetailModal title={getModalTitle(activeModal)} onClose={() => setActiveModal(null)}>
          {activeModal === 'person' && person && <PersonDetail person={person} leadName={`${lead.firstName} ${lead.lastName}`.trim()} />}
          {activeModal === 'signals' && <SignalsDetail signals={buyerSignals} />}
          {activeModal === 'financial' && (financialCapacity || neighborhoodData) && <FinancialDetail data={financialCapacity || { combinedAssets: 0, capacityLevel: 'unknown', hasMortgageOfRecord: false, cashBuyerIndicators: false, constructionLoanMentioned: false, flags: [] } as any} neighborhoodData={neighborhoodData} avm={avm} />}
          {activeModal === 'property' && <PropertyDetail property={property} avm={avm} ownership={ownership} saleHistory={saleHistory} buildSite={buildSite} permits={permits} location={lead.location} person={person} />}
          {activeModal === 'engagement' && behavioral && <EngagementDetail data={behavioral} recentEvents={lead.recentEvents} />}
          {activeModal === 'briefs' && <BriefsDetail points={talkingPoints} />}
          {activeModal === 'score' && <ScoreDetail score={score} />}
        </DetailModal>
      )}
    </div>
  );
};

// ═══ Person Card (inline) ═══

function PersonCard({ person, leadName, onViewMore }: { person: PersonEnrichment; leadName?: string; onViewMore: () => void }) {
  const name = person.fullName || leadName;
  const searchHref = buildLinkedInSearch(name);
  const hasLinkedIn = !!(person.linkedinUrl || searchHref);

  return (
    <div className="lip2-person">
      <div className="lip2-person-main">
        {person.jobTitle && (
          <div className="lip2-person-job">
            <Briefcase size={12} />
            <span className="lip2-person-title">{person.jobTitle}</span>
          </div>
        )}
        {person.jobCompany && (
          <div className="lip2-person-co">
            {person.jobCompany}
            {person.jobCompanyIndustry && <span className="lip2-person-dim"> · {person.jobCompanyIndustry}</span>}
          </div>
        )}
        {person.homeAddress && hasRealAddress(person.homeAddress) && (
          <div className="lip2-person-addr">
            <MapPin size={10} />
            {[person.homeAddress.street, person.homeAddress.city, person.homeAddress.state].filter(v => typeof v === 'string' && v.length > 1).join(', ')}
          </div>
        )}
      </div>
      <div className="lip2-person-links">
        {hasLinkedIn && searchHref && (
          <a href={searchHref} target="_blank" rel="noopener noreferrer" className="lip2-li-btn" title={`Search LinkedIn for ${name}`}>
            <Linkedin size={13} />
          </a>
        )}
        {person.socialProfiles?.map(p => {
          const Icon = getSocialIcon(p.network);
          return (
            <a key={p.network} href={ensureUrl(p.url)} target="_blank" rel="noopener noreferrer" className={`lip2-social-icon lip2-social-${p.network}`} title={`${p.network}: ${p.username || ''}`}>
              <Icon size={13} />
            </a>
          );
        })}
        <button className="lip2-more-btn" onClick={onViewMore} title="View full profile">
          <ChevronRight size={13} />
        </button>
      </div>
    </div>
  );
}

// ═══ Detail Modal ═══

function DetailModal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  return (
    <div className="lip2-modal-overlay" ref={overlayRef} onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}>
      <div className="lip2-modal">
        <div className="lip2-modal-header">
          <span className="lip2-modal-title">{title}</span>
          <button className="lip2-modal-close" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="lip2-modal-body">
          {children}
        </div>
      </div>
    </div>
  );
}

// ═══ Modal Content: Person ═══

function PersonDetail({ person, leadName }: { person: PersonEnrichment; leadName?: string }) {
  const name = person.fullName || leadName;
  const searchHref = buildLinkedInSearch(name);
  const directHref = buildLinkedInDirect(person.linkedinUrl);

  return (
    <div className="lip2-md-person">
      {person.jobTitle && (
        <div className="lip2-md-row lip2-md-row-featured">
          <Briefcase size={14} />
          <div>
            <div className="lip2-md-primary">{person.jobTitle}</div>
            {person.jobCompany && (
              <div className="lip2-md-secondary">
                {person.jobCompany}
                {person.jobCompanyIndustry && ` · ${person.jobCompanyIndustry}`}
                {person.jobCompanySize && ` · ${person.jobCompanySize} employees`}
              </div>
            )}
          </div>
        </div>
      )}

      {person.inferredSalary && (
        <div className="lip2-md-row">
          <DollarSign size={14} />
          <div>
            <div className="lip2-md-label">Estimated Income</div>
            <div className="lip2-md-value lip2-md-green">{person.inferredSalary}</div>
          </div>
        </div>
      )}

      {person.homeAddress && hasRealAddress(person.homeAddress) && (
        <div className="lip2-md-row">
          <MapPin size={14} />
          <div>
            <div className="lip2-md-label">Home Address</div>
            <div className="lip2-md-value">
              {[person.homeAddress.street, person.homeAddress.city, person.homeAddress.state, person.homeAddress.postalCode]
                .filter(v => typeof v === 'string' && v.length > 1).join(', ')}
            </div>
          </div>
        </div>
      )}

      {person.education && person.education.length > 0 && person.education[0].school && (
        <div className="lip2-md-row">
          <GraduationCap size={14} />
          <div>
            <div className="lip2-md-label">Education</div>
            <div className="lip2-md-value">
              {person.education.filter(e => e.school).map((e, i) => (
                <div key={i}>{e.degree ? `${e.degree}, ` : ''}{e.school}{e.major ? ` — ${e.major}` : ''}</div>
              ))}
            </div>
          </div>
        </div>
      )}

      {person.ageRange && (
        <div className="lip2-md-row">
          <Clock size={14} />
          <div>
            <div className="lip2-md-label">Age Range</div>
            <div className="lip2-md-value">{person.ageRange}</div>
          </div>
        </div>
      )}

      {person.isBusinessOwner && (
        <div className="lip2-md-row lip2-md-row-featured">
          <Building2 size={14} />
          <div>
            <div className="lip2-md-primary">Business Owner</div>
            {person.businessName && <div className="lip2-md-secondary">{person.businessName}</div>}
          </div>
        </div>
      )}

      {person.newsMentions && person.newsMentions > 0 ? (
        <div className="lip2-md-row">
          <Newspaper size={14} />
          <div>
            <div className="lip2-md-label">News & Press</div>
            <div className="lip2-md-value">{person.newsMentions} mention{person.newsMentions !== 1 ? 's' : ''} found</div>
          </div>
        </div>
      ) : null}

      {(searchHref || directHref || person.socialProfiles?.length || person.facebookUrl) && (
        <div className="lip2-md-socials">
          {searchHref && (
            <a href={searchHref} target="_blank" rel="noopener noreferrer" className="lip2-md-social-btn lip2-md-social-li">
              <Linkedin size={12} /> Find on LinkedIn
            </a>
          )}
          {directHref && (
            <a href={directHref} target="_blank" rel="noopener noreferrer" className="lip2-md-social-btn" title="Direct profile link from PDL (may be outdated)">
              <Globe2 size={12} /> Direct Profile
            </a>
          )}
          {person.socialProfiles?.map(p => {
            const Icon = getSocialIcon(p.network);
            return (
              <a key={p.network} href={ensureUrl(p.url)} target="_blank" rel="noopener noreferrer" className={`lip2-md-social-btn lip2-md-social-${p.network}`}>
                <Icon size={12} /> {p.network.charAt(0).toUpperCase() + p.network.slice(1)}{p.username ? ` @${p.username}` : ''}
              </a>
            );
          })}
          {person.facebookUrl && !person.socialProfiles?.some(p => p.network === 'facebook') && (
            <a href={ensureUrl(person.facebookUrl)} target="_blank" rel="noopener noreferrer" className="lip2-md-social-btn lip2-md-social-facebook">
              <Facebook size={12} /> Facebook
            </a>
          )}
        </div>
      )}

      {person.likelihood !== undefined && (
        <div className="lip2-md-confidence">Match confidence: {person.likelihood}/10</div>
      )}
    </div>
  );
}

// ═══ Modal Content: Signals ═══

function SignalsDetail({ signals }: { signals: TimberFrameBuyerSignal[] }) {
  return (
    <div className="lip2-md-signals">
      {signals.map(s => (
        <div key={s.id} className="lip2-md-signal">
          <span className="lip2-md-signal-dot" style={{ background: getCategoryColor(s.category) }} />
          <div>
            <div className="lip2-md-signal-label">{s.label}</div>
            <div className="lip2-md-signal-desc">{s.description}</div>
          </div>
          {s.scoreImpact > 0 && <span className="lip2-md-signal-pts">+{s.scoreImpact}</span>}
        </div>
      ))}
    </div>
  );
}

// ═══ Modal Content: Financial ═══

function FinancialDetail({ data, neighborhoodData, avm }: {
  data: NonNullable<LeadEnrichment['financialCapacity']>;
  neighborhoodData?: LeadEnrichment['neighborhoodData'];
  avm?: LeadEnrichment['avm'];
}) {
  const isWebEstimate = avm?.source && avm.source !== 'attom';

  return (
    <div className="lip2-md-financial">
      <div className="lip2-md-fin-hero">
        <div className="lip2-md-fin-label">
          Combined Real Estate Assets
          {isWebEstimate && <span className="lip2-md-fin-web-tag">Estimated from web</span>}
        </div>
        <div className="lip2-md-fin-value" style={{ color: getCapacityColor(data.capacityLevel) }}>
          {data.combinedAssets > 0 ? formatCurrency(data.combinedAssets) : 'Unknown'}
        </div>
      </div>
      <div className="lip2-md-fin-rows">
        {data.primaryHomeValue && <FinRow label="Primary Home" value={formatCurrency(data.primaryHomeValue)} />}
        {data.landValue && <FinRow label="Land Value" value={formatCurrency(data.landValue)} />}
        {data.otherRealEstateValue && <FinRow label="Other Real Estate" value={formatCurrency(data.otherRealEstateValue)} />}
        <FinRow label="Mortgage" value={data.hasMortgageOfRecord ? 'On record' : 'None found'} />
        {data.constructionLoanMentioned && <FinRow label="Financing" value="Construction loan mentioned" highlight />}
        {data.cashBuyerIndicators && <FinRow label="Cash Buyer" value="Indicators present" highlight />}
      </div>

      {neighborhoodData && (neighborhoodData.medianHouseholdIncome || neighborhoodData.medianHomeValue) && (
        <div className="lip2-md-neighborhood">
          <div className="lip2-md-neighborhood-title">
            <Users size={13} />
            Neighborhood Context
            {neighborhoodData.censusTract && <span className="lip2-md-neighborhood-tract">Tract {neighborhoodData.censusTract}</span>}
          </div>
          <div className="lip2-md-fin-rows">
            {neighborhoodData.medianHouseholdIncome && (
              <FinRow label="Median Household Income" value={formatCurrency(neighborhoodData.medianHouseholdIncome)} />
            )}
            {neighborhoodData.medianHomeValue && (
              <FinRow label="Median Home Value" value={formatCurrency(neighborhoodData.medianHomeValue)} />
            )}
            {neighborhoodData.bachelorsDegreeOrHigherPct !== null && neighborhoodData.bachelorsDegreeOrHigherPct !== undefined && (
              <FinRow label="Bachelor's Degree+" value={`${neighborhoodData.bachelorsDegreeOrHigherPct}%`} />
            )}
          </div>
          <div className="lip2-md-neighborhood-source">Source: US Census ACS</div>
        </div>
      )}

      {data.flags.length > 0 && (
        <div className="lip2-md-fin-flags">
          {data.flags.map((flag, i) => (
            <div key={i} className="lip2-md-fin-flag">
              <AlertTriangle size={11} />
              {flag}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FinRow({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className={`lip2-md-fin-row ${highlight ? 'lip2-md-fin-highlight' : ''}`}>
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}

// ═══ Modal Content: Property ═══

function PropertyDetail({ property, avm, ownership, saleHistory, buildSite, permits, location, person }: {
  property?: LeadEnrichment['property'];
  avm?: LeadEnrichment['avm'];
  ownership?: LeadEnrichment['ownership'];
  saleHistory?: LeadEnrichment['saleHistory'];
  buildSite?: LeadEnrichment['buildSite'];
  permits?: LeadEnrichment['permits'];
  location?: string;
  person?: PersonEnrichment;
}) {
  const homeAddr = person?.homeAddress;
  const addrStr = homeAddr
    ? [homeAddr.street, homeAddr.city, homeAddr.state, homeAddr.postalCode]
        .filter(v => typeof v === 'string' && v.length > 1).join(', ')
    : null;

  return (
    <div className="lip2-md-property">
      {addrStr && (
        <div className="lip2-md-home-addr">
          <Home size={14} />
          <span>{addrStr}</span>
        </div>
      )}

      {buildSite?.aerialImageUrl && (
        <div className="lip2-md-aerial">
          <img src={buildSite.aerialImageUrl} alt="Aerial view" />
          <div className="lip2-md-aerial-label">
            <MapPin size={11} />
            {addrStr || location || 'Build Site'}
          </div>
        </div>
      )}

      {!avm?.value && addrStr && (
        <a
          href={`https://www.zillow.com/homes/${encodeURIComponent(addrStr)}_rb/`}
          target="_blank"
          rel="noopener noreferrer"
          className="lip2-md-zillow-link"
        >
          <Globe2 size={13} />
          Look up on Zillow
        </a>
      )}

      {avm && avm.value && (
        <div className="lip2-md-avm">
          <div className="lip2-md-avm-label">
            {avm.source && avm.source !== 'attom' ? 'Estimated Value' : 'Automated Valuation'}
            {avm.source && avm.source !== 'attom' && <span className="lip2-md-fin-web-tag">via {avm.source}</span>}
          </div>
          <div className="lip2-md-avm-value">{formatCurrency(avm.value)}</div>
          {(avm.low || avm.high) && (
            <div className="lip2-md-avm-range">{avm.low && formatCurrency(avm.low)} — {avm.high && formatCurrency(avm.high)}</div>
          )}
        </div>
      )}

      {property && (
        <div className="lip2-md-prop-grid">
          {property.propertyType !== 'unknown' && <PropCell label="Type" value={formatPropertyType(property.propertyType)} />}
          {property.lotSizeAcres && <PropCell label="Lot Size" value={`${property.lotSizeAcres} acres`} />}
          {property.lotSizeSqft && !property.lotSizeAcres && <PropCell label="Lot Size" value={`${(property.lotSizeSqft / 43560).toFixed(2)} acres`} />}
          {property.buildingSqft && <PropCell label="Building" value={`${property.buildingSqft.toLocaleString()} sqft`} />}
          {property.yearBuilt && <PropCell label="Year Built" value={`${property.yearBuilt}`} />}
          {property.bedrooms && <PropCell label="Beds" value={`${property.bedrooms}`} />}
          {property.bathrooms && <PropCell label="Baths" value={`${property.bathrooms}`} />}
          {property.constructionType && <PropCell label="Construction" value={property.constructionType} />}
          {property.assessedValue && <PropCell label="Assessed" value={formatCurrency(property.assessedValue)} />}
        </div>
      )}

      {ownership && (
        <>
          <div className="lip2-md-section-label">Ownership</div>
          <div className="lip2-md-prop-grid">
            {ownership.ownerName && <PropCell label="Owner" value={ownership.ownerName} />}
            {ownership.ownershipStatus !== 'unknown' && <PropCell label="Status" value={formatOwnershipStatus(ownership.ownershipStatus)} />}
            {ownership.purchasePrice && <PropCell label="Purchase Price" value={formatCurrency(ownership.purchasePrice)} />}
            {ownership.yearsOfOwnership !== undefined && <PropCell label="Owned" value={`${ownership.yearsOfOwnership} yrs`} />}
            {ownership.estimatedEquity !== undefined && ownership.estimatedEquity > 0 && <PropCell label="Est. Equity" value={formatCurrency(ownership.estimatedEquity)} highlight />}
          </div>
        </>
      )}

      {buildSite && (
        <div className="lip2-md-site-tags">
          <SiteTag label={`Flood: ${formatFloodZone(buildSite.floodZone)}`} variant={buildSite.floodZone === 'none' ? 'good' : buildSite.floodZone === 'unknown' ? 'neutral' : 'warn'} />
          {buildSite.isRural && <SiteTag label="Rural" variant="good" />}
          {buildSite.isScenicArea && <SiteTag label="Scenic" variant="good" />}
          {buildSite.roadAccess !== undefined && <SiteTag label={buildSite.roadAccess ? 'Road Access' : 'No Road'} variant={buildSite.roadAccess ? 'good' : 'warn'} />}
        </div>
      )}

      {saleHistory && saleHistory.length > 0 && (
        <>
          <div className="lip2-md-section-label">Sale History</div>
          {saleHistory.slice(0, 5).map((sale, i) => (
            <div key={i} className="lip2-md-sale-row">
              <span>{sale.date ? new Date(sale.date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '—'}</span>
              <span className="lip2-md-sale-amt">{sale.amount ? formatCurrency(sale.amount) : '—'}</span>
            </div>
          ))}
        </>
      )}

      {permits && permits.permits.length > 0 && (
        <>
          <div className="lip2-md-section-label">Permits ({permits.recentPermitCount})</div>
          {permits.permits.slice(0, 5).map((p, i) => (
            <div key={i} className="lip2-md-permit-row">
              <span className="lip2-md-permit-type">{p.type}</span>
              <span className="lip2-md-permit-val">{p.value ? formatCurrency(p.value) : '—'}</span>
              <span className="lip2-md-permit-date">{new Date(p.dateIssued).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</span>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

function PropCell({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className={`lip2-md-prop-cell ${highlight ? 'lip2-md-prop-hl' : ''}`}>
      <span className="lip2-md-prop-cell-label">{label}</span>
      <span className="lip2-md-prop-cell-value">{value}</span>
    </div>
  );
}

function SiteTag({ label, variant }: { label: string; variant: 'good' | 'warn' | 'neutral' }) {
  return <span className={`lip2-md-site-tag lip2-md-st-${variant}`}>{label}</span>;
}

// ═══ Modal Content: Engagement ═══

function EngagementDetail({ data, recentEvents }: { data: NonNullable<LeadEnrichment['behavioral']>; recentEvents?: Array<{ url?: string; type?: string; createdAt?: any }> }) {
  const uniquePages = React.useMemo(() => {
    if (!recentEvents?.length) return [];
    const seen = new Map<string, { url: string; count: number; lastVisit: Date }>();
    for (const event of recentEvents) {
      if (!event.url) continue;
      try {
        const urlObj = new URL(event.url, 'https://example.com');
        const path = urlObj.pathname;
        if (path === '/' || path.length < 2) continue;
        const existing = seen.get(path);
        const raw: any = event.createdAt;
        const eventDate = raw instanceof Date ? raw
          : (raw && typeof raw === 'object' && 'toDate' in raw) ? raw.toDate()
          : new Date(raw);
        if (existing) {
          existing.count++;
          if (!isNaN(eventDate?.getTime?.()) && eventDate > existing.lastVisit) existing.lastVisit = eventDate;
        } else {
          seen.set(path, { url: event.url, count: 1, lastVisit: isNaN(eventDate?.getTime?.()) ? new Date() : eventDate });
        }
      } catch { /* skip invalid urls */ }
    }
    return Array.from(seen.entries())
      .map(([path, d]) => ({ path, ...d }))
      .sort((a, b) => b.count - a.count);
  }, [recentEvents]);

  const formatPath = (path: string) => {
    return path
      .replace(/^\//, '')
      .replace(/[-_]/g, ' ')
      .replace(/\//g, ' / ')
      .replace(/\b\w/g, c => c.toUpperCase())
      .substring(0, 50);
  };

  return (
    <div className="lip2-md-engagement">
      <div className="lip2-md-eng-stats">
        <EngBox label="Visits" value={`${data.returnVisitCount + 1}`} />
        <EngBox label="Pages" value={`${data.totalPagesViewed}`} />
        <EngBox label="Days Active" value={`${data.daysBetweenFirstAndLastVisit}`} />
        <EngBox label="Avg Depth" value={`${data.averageSessionDepthPages.toFixed(1)}`} />
      </div>

      <div className="lip2-md-eng-pages">
        {data.floorPlanPageViews > 0 && <span className="lip2-md-eng-pill hot">Floor Plans: {data.floorPlanPageViews}</span>}
        {data.processPageViews > 0 && <span className="lip2-md-eng-pill hot">Process: {data.processPageViews}</span>}
        {data.pricingPageViews > 0 && <span className="lip2-md-eng-pill hot">Pricing: {data.pricingPageViews}</span>}
        {data.testimonialsPageViews > 0 && <span className="lip2-md-eng-pill">Testimonials: {data.testimonialsPageViews}</span>}
      </div>

      {data.portfolioProjectsViewed.length > 0 && (
        <>
          <div className="lip2-md-section-label">Portfolio Projects Studied</div>
          {data.portfolioProjectsViewed.slice(0, 6).map((pv, i) => (
            <div key={pv.projectName} className="lip2-md-portfolio-row">
              <span className="lip2-md-portfolio-rank">{i + 1}</span>
              <span className="lip2-md-portfolio-name">{pv.projectName}</span>
              <span className="lip2-md-portfolio-time">{Math.round(pv.totalDwellTimeSeconds / 60)}m</span>
              <span className="lip2-md-portfolio-views">{pv.viewCount}x</span>
            </div>
          ))}
        </>
      )}

      {uniquePages.length > 0 && (
        <>
          <div className="lip2-md-section-label">Pages Visited</div>
          <div className="lip2-md-pages-list">
            {uniquePages.slice(0, 20).map((page) => (
              <div key={page.path} className="lip2-md-page-row">
                <Globe2 size={11} className="lip2-md-page-icon" />
                <span className="lip2-md-page-path">{formatPath(page.path)}</span>
                {page.count > 1 && <span className="lip2-md-page-count">{page.count}x</span>}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function EngBox({ label, value }: { label: string; value: string }) {
  return (
    <div className="lip2-md-eng-box">
      <span className="lip2-md-eng-box-val">{value}</span>
      <span className="lip2-md-eng-box-label">{label}</span>
    </div>
  );
}

// ═══ Modal Content: Briefs ═══

function BriefsDetail({ points }: { points: LeadEnrichment['talkingPoints'] }) {
  return (
    <div className="lip2-md-briefs">
      {points.map((tp, i) => (
        <div key={i} className={`lip2-md-brief lip2-md-brief-${tp.category}`}>
          {tp.text}
        </div>
      ))}
    </div>
  );
}

// ═══ Modal Content: Score ═══

function ScoreDetail({ score }: { score: LeadEnrichment['score'] }) {
  const bd = score?.breakdown;
  if (!score?.band || !bd) return null;
  return (
    <div className="lip2-md-score">
      <div className="lip2-md-score-total">
        <span className="lip2-md-score-num" style={{ color: getScoreBandColor(score.band) }}>{score.total}</span>
        <span className="lip2-md-score-max">/100</span>
      </div>
      <div className="lip2-md-score-bars">
        <ScoreBar label="Build Site" value={bd.buildSiteQualification ?? 0} max={25} />
        <ScoreBar label="Project Readiness" value={bd.projectReadiness ?? 0} max={30} />
        <ScoreBar label="Life / Financial" value={bd.lifeChapterFinancial ?? 0} max={25} />
        <ScoreBar label="Engagement" value={bd.behavioralEngagement ?? 0} max={20} />
      </div>
      {score.penalties != null && score.penalties < 0 && (
        <div className="lip2-md-penalties">
          {(score.penaltyReasons ?? []).map((reason, i) => (
            <div key={i} className="lip2-md-penalty">
              <AlertTriangle size={11} />
              {reason}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ScoreBar({ label, value, max }: { label: string; value: number; max: number }) {
  const pct = Math.min((value / max) * 100, 100);
  return (
    <div className="lip2-md-sbar">
      <div className="lip2-md-sbar-header">
        <span>{label}</span>
        <span className="lip2-md-sbar-pts">{value}/{max}</span>
      </div>
      <div className="lip2-md-sbar-track">
        <div className="lip2-md-sbar-fill" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

// ═══ Utilities ═══

function parseFirestoreDate(val: any): Date {
  if (val instanceof Date) return val;
  if (val?.toDate && typeof val.toDate === 'function') return val.toDate();
  if (val?.seconds) return new Date(val.seconds * 1000);
  if (typeof val === 'string' || typeof val === 'number') return new Date(val);
  return new Date();
}

function getTimeAgo(date: Date): string {
  const diffMs = Date.now() - date.getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return days === 1 ? 'yesterday' : `${days}d ago`;
}

function buildLinkedInSearch(personName?: string): string | null {
  if (!personName) return null;
  return `https://www.linkedin.com/search/results/all/?keywords=${encodeURIComponent(personName)}`;
}

function buildLinkedInDirect(raw?: string): string | null {
  if (!raw || typeof raw !== 'string') return null;
  const cleaned = raw.trim();
  if (!cleaned) return null;
  if (cleaned.startsWith('http://') || cleaned.startsWith('https://')) return cleaned;
  if (cleaned.startsWith('linkedin.com') || cleaned.startsWith('www.linkedin.com')) return `https://${cleaned}`;
  if (cleaned.includes('/in/')) return `https://linkedin.com${cleaned.startsWith('/') ? '' : '/'}${cleaned}`;
  return `https://www.linkedin.com/in/${cleaned}`;
}

function ensureUrl(url: string): string {
  return url.startsWith('http') ? url : `https://${url}`;
}

function getSocialIcon(network: string) {
  switch (network.toLowerCase()) {
    case 'facebook': return Facebook;
    case 'instagram': return Instagram;
    case 'twitter': return Twitter;
    case 'youtube': return Youtube;
    default: return Globe2;
  }
}

function hasRealAddress(addr: { street?: string; city?: string; state?: string }): boolean {
  const hasStr = (v: any) => typeof v === 'string' && v.length > 1;
  return hasStr(addr.city) || hasStr(addr.street);
}

function formatCompact(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${Math.round(n / 1_000)}K`;
  return `$${n}`;
}

function getCategoryColor(cat: string): string {
  const map: Record<string, string> = {
    readiness: '#16a34a',
    life_chapter: '#7c3aed',
    aesthetic: '#0891b2',
    financial: '#ca8a04',
    competitive: '#2563eb',
  };
  return map[cat] || '#6b7280';
}

function getModalTitle(key: string): string {
  const map: Record<string, string> = {
    person: 'Person Profile',
    signals: 'Buyer Signals',
    financial: 'Financial Capacity',
    property: 'Property & Site',
    engagement: 'Website Engagement',
    briefs: 'Sales Brief',
    score: 'Score Breakdown',
  };
  return map[key] || key;
}

function formatPropertyType(type: string): string {
  const map: Record<string, string> = { sfr: 'Single Family', multi_family: 'Multi-Family', commercial: 'Commercial', vacant_land: 'Vacant Land', agricultural: 'Agricultural' };
  return map[type] || type;
}

function formatOwnershipStatus(status: string): string {
  const map: Record<string, string> = { owner_occupied: 'Owner-Occupied', absentee: 'Absentee Owner', investor: 'Investor' };
  return map[status] || status;
}

function formatFloodZone(zone: string): string {
  const map: Record<string, string> = { none: 'None', moderate: 'Moderate', high: 'High Risk', coastal_high: 'Coastal High', unknown: 'Unknown' };
  return map[zone] || zone;
}

export default LeadIntelligencePanel;
